import type { LearningDomain, Subject } from '@/content/schema';
import { bank, lesson, m, objective, opts, SAMPLE, type ObjectiveInput } from '../helpers';

export const subject: Subject = {
  id: 'english',
  name: { ar: 'الإنقليزيّة', fr: 'Anglais', en: 'English' },
  glyph: 'En',
  color: 'english',
  teachingLanguage: 'en',
  levels: ['y4', 'y5', 'y6'],
  availabilityNote: {
    ar: 'صفحة البرامج الرسميّة تُدرج الإنقليزيّة في الدرجة الثالثة، وأُعلن في 2019 عن تدريسها من السنة الرابعة. يجب التثبّت من ذلك.',
    fr: 'La page des programmes officiels place l’anglais au 3e degré ; une annonce de 2019 l’a avancé en 4e année. À vérifier.',
    en: 'The official programmes page lists English in the third degree; a 2019 announcement moved it to year 4. To be verified.',
  },
  sources: [{ sourceId: 'tn-moe-english-degree3' }, { sourceId: 'tn-2019-languages-announcement' }],
  ...SAMPLE,
};

export const domains: LearningDomain[] = [
  {
    id: 'en.letters',
    subjectId: 'english',
    name: { ar: 'الحروف', fr: 'Alphabet', en: 'Alphabet' },
  },
  {
    id: 'en.vocabulary',
    subjectId: 'english',
    name: { ar: 'المفردات', fr: 'Vocabulaire', en: 'Vocabulary' },
  },
  {
    id: 'en.communication',
    subjectId: 'english',
    name: { ar: 'التواصل', fr: 'Communication', en: 'Communication' },
  },
  {
    id: 'en.grammar',
    subjectId: 'english',
    name: { ar: 'القواعد', fr: 'Grammaire', en: 'Grammar' },
  },
];

const o = (input: ObjectiveInput) => objective('english', 'en', input);
const ENGLISH_REF = [
  {
    sourceId: 'tn-moe-english-degree3',
    note: 'Communicative functions and topics inspired by this programme.',
  },
];

export const objectives = [
  o({
    id: 'en.alphabet',
    domainId: 'en.letters',
    levels: ['y4'],
    order: 10,
    difficulty: 1,
    title: { ar: 'الحروف الإنقليزيّة', fr: 'L’alphabet anglais', en: 'The alphabet' },
    description: {
      ar: 'يتعرّف على الحروف الكبيرة والصغيرة وترتيبها.',
      fr: 'Reconnaître majuscules, minuscules et ordre des lettres.',
      en: 'Recognise capital and small letters and their order.',
    },
    games: ['quick-quiz', 'find-correct', 'memory', 'true-false', 'letter-sound'],
  }),
  o({
    id: 'en.colours',
    domainId: 'en.vocabulary',
    levels: ['y4'],
    order: 20,
    difficulty: 1,
    title: { ar: 'الألوان', fr: 'Les couleurs', en: 'Colours' },
    description: {
      ar: 'يسمّي الألوان بالإنقليزيّة.',
      fr: 'Nommer les couleurs en anglais.',
      en: 'Name colours in English.',
    },
    games: ['quick-quiz', 'find-correct', 'memory', 'true-false'],
  }),
  o({
    id: 'en.numbers',
    domainId: 'en.vocabulary',
    levels: ['y4', 'y5'],
    order: 30,
    difficulty: 1,
    title: { ar: 'الأعداد بالإنقليزيّة', fr: 'Les nombres en anglais', en: 'Numbers' },
    description: {
      ar: 'يقرأ الأعداد من 1 إلى 20 ويكتبها بالإنقليزيّة.',
      fr: 'Lire et écrire les nombres de 1 à 20.',
      en: 'Read and write numbers from 1 to 20.',
    },
    games: ['quick-quiz', 'find-correct', 'memory', 'true-false'],
  }),
  o({
    id: 'en.family-animals',
    domainId: 'en.vocabulary',
    levels: ['y5'],
    order: 40,
    difficulty: 2,
    prerequisites: ['en.colours'],
    title: { ar: 'العائلة والحيوانات', fr: 'La famille et les animaux', en: 'Family and pets' },
    description: {
      ar: 'يسمّي أفراد العائلة والحيوانات الأليفة.',
      fr: 'Nommer les membres de la famille et les animaux.',
      en: 'Name family members and pets.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'memory'],
  }),
  o({
    id: 'en.greetings',
    domainId: 'en.communication',
    levels: ['y5'],
    order: 50,
    difficulty: 2,
    title: {
      ar: 'التحيّة والتعارف',
      fr: 'Saluer et se présenter',
      en: 'Greetings and introductions',
    },
    description: {
      ar: 'يختار الردّ المناسب في حوار قصير.',
      fr: 'Choisir la bonne réponse dans un court dialogue.',
      en: 'Choose the right reply in a short dialogue.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'en.to-be',
    domainId: 'en.grammar',
    levels: ['y6'],
    order: 60,
    difficulty: 3,
    prerequisites: ['en.greetings'],
    title: { ar: 'الفعل to be', fr: 'Le verbe to be', en: 'The verb to be' },
    description: {
      ar: 'يختار am أو is أو are.',
      fr: 'Choisir am, is ou are.',
      en: 'Choose am, is or are.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'en.days-months',
    domainId: 'en.vocabulary',
    levels: ['y6'],
    order: 70,
    difficulty: 2,
    prerequisites: ['en.numbers'],
    title: { ar: 'الأيّام والأشهر', fr: 'Les jours et les mois', en: 'Days and months' },
    description: {
      ar: 'يتعرّف على أيّام الأسبوع وأشهر السنة وترتيبها.',
      fr: 'Connaître les jours de la semaine et les mois.',
      en: 'Know the days of the week and the months.',
    },
    games: ['categorize', 'sentence-builder', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'en.sentences',
    domainId: 'en.grammar',
    levels: ['y6'],
    order: 80,
    difficulty: 3,
    prerequisites: ['en.to-be'],
    title: {
      ar: 'أركّب جملة إنقليزيّة',
      fr: 'Construire une phrase en anglais',
      en: 'Building sentences',
    },
    description: {
      ar: 'يرتّب الكلمات ليكوّن جملة إنقليزيّة.',
      fr: 'Ordonner des mots pour former une phrase.',
      en: 'Put words in order to make a sentence.',
    },
    games: ['sentence-builder', 'quick-quiz'],
  }),
].map((objective) => ({ ...objective, sources: ENGLISH_REF }));

export const items = [
  ...bank('en.alphabet', 1, [
    {
      kind: 'choice',
      prompt: 'Which is the small letter of « B »?',
      ...opts('b', m('d', 'similar-letter-shape'), m('p', 'similar-letter-shape')),
    },
    {
      kind: 'choice',
      prompt: 'Which is the capital letter of « e »?',
      ...opts('E', m('F', 'similar-letter-shape'), m('A', 'unknown')),
    },
    {
      kind: 'choice',
      prompt: 'Which letter comes after « c »?',
      ...opts('d', m('b', 'letter-order'), m('e', 'letter-order')),
    },
    {
      kind: 'choice',
      prompt: 'Which letter comes before « n »?',
      ...opts('m', m('o', 'letter-order'), m('l', 'letter-order')),
    },
    {
      kind: 'choice',
      prompt: 'Which word begins with « s »?',
      audio: { text: 'sun' },
      ...opts(
        { text: 'sun', emoji: '☀️' },
        m('cat', 'similar-sound', '🐱'),
        m('zoo', 'similar-sound', '🦁'),
      ),
    },
    { kind: 'trueFalse', statement: '« Q » is the capital letter of « q ».', answer: true },
    {
      kind: 'trueFalse',
      statement: '« d » is the small letter of « B ».',
      answer: false,
      mistake: 'similar-letter-shape',
      explanation: 'The small letter of « B » is « b ».',
    },
    {
      kind: 'match',
      prompt: 'Match the capital letters with the small letters.',
      pairs: [
        { left: 'A', right: 'a' },
        { left: 'B', right: 'b' },
        { left: 'D', right: 'd' },
        { left: 'G', right: 'g' },
        { left: 'M', right: 'm' },
        { left: 'R', right: 'r' },
      ],
      mistake: 'similar-letter-shape',
    },
  ]),
  ...bank('en.colours', 1, [
    {
      kind: 'choice',
      prompt: 'What colour is it?',
      visual: { emoji: '🍓', label: 'strawberry' },
      ...opts('red', m('blue', 'vocabulary'), m('green', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'What colour is it?',
      visual: { emoji: '🍌', label: 'banana' },
      ...opts('yellow', m('purple', 'vocabulary'), m('white', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'What colour is it?',
      visual: { emoji: '🐸', label: 'frog' },
      ...opts('green', m('orange', 'vocabulary'), m('black', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'What colour is it?',
      visual: { emoji: '🍊', label: 'orange' },
      ...opts('orange', m('red', 'vocabulary'), m('yellow', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'What colour is the sky on a sunny day?',
      ...opts('blue', m('green', 'vocabulary'), m('brown', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'What colour is milk?',
      visual: { emoji: '🥛', label: 'milk' },
      ...opts('white', m('black', 'vocabulary'), m('pink', 'vocabulary')),
    },
    { kind: 'trueFalse', statement: 'Grass is green.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'Snow is black.',
      answer: false,
      mistake: 'vocabulary',
      explanation: 'Snow is white.',
    },
    {
      kind: 'match',
      prompt: 'Match each picture with its colour.',
      pairs: [
        { left: '🍓', right: 'red' },
        { left: '🍌', right: 'yellow' },
        { left: '🐸', right: 'green' },
        { left: '🍆', right: 'purple' },
        { left: '🥛', right: 'white' },
        { left: '🍊', right: 'orange' },
      ],
      mistake: 'vocabulary',
    },
  ]),
  ...bank('en.numbers', 1, [
    {
      kind: 'choice',
      prompt: 'What number is « seven »?',
      ...opts('7', m('6', 'vocabulary'), m('9', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'How do we write 12 in English?',
      ...opts('twelve', m('twenty', 'vocabulary'), m('two', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'What number is « fifteen »?',
      ...opts('15', m('50', 'vocabulary'), m('5', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'How do we write 3 in English?',
      ...opts('three', m('tree', 'spelling'), m('thirteen', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'What number is « twenty »?',
      ...opts('20', m('12', 'vocabulary'), m('2', 'vocabulary')),
    },
    { kind: 'trueFalse', statement: '« fifteen » is 15.', answer: true },
    {
      kind: 'trueFalse',
      statement: '« thirteen » is 30.',
      answer: false,
      mistake: 'vocabulary',
      explanation: '« thirteen » is 13. 30 is « thirty ».',
    },
    {
      kind: 'match',
      prompt: 'Match the numbers with the words.',
      pairs: [
        { left: '1', right: 'one' },
        { left: '4', right: 'four' },
        { left: '8', right: 'eight' },
        { left: '10', right: 'ten' },
        { left: '11', right: 'eleven' },
        { left: '20', right: 'twenty' },
      ],
      mistake: 'vocabulary',
    },
  ]),
  ...bank('en.family-animals', 2, [
    {
      kind: 'categorize',
      prompt: 'Sort the words.',
      categories: [
        { id: 'family', label: 'Family' },
        { id: 'animals', label: 'Animals' },
      ],
      entries: [
        { text: 'mother', category: 'family' },
        { text: 'brother', category: 'family' },
        { text: 'sister', category: 'family' },
        { text: 'father', category: 'family' },
        { text: 'dog', emoji: '🐶', category: 'animals' },
        { text: 'cat', emoji: '🐱', category: 'animals' },
        { text: 'bird', emoji: '🐦', category: 'animals' },
        { text: 'horse', emoji: '🐴', category: 'animals' },
      ],
      mistake: 'vocabulary',
    },
    {
      kind: 'choice',
      prompt: 'Your mother’s mother is your…',
      ...opts('grandmother', m('sister', 'vocabulary'), m('aunt', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Your father’s son is your…',
      ...opts('brother', m('uncle', 'vocabulary'), m('cousin', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Which animal is a pet?',
      ...opts(
        { text: 'a cat', emoji: '🐱' },
        m('a lion', 'vocabulary', '🦁'),
        m('a camel', 'vocabulary', '🐪'),
      ),
    },
    {
      kind: 'choice',
      prompt: 'What is it?',
      visual: { emoji: '🐰', label: 'rabbit' },
      ...opts('a rabbit', m('a horse', 'vocabulary'), m('a fish', 'vocabulary')),
    },
    {
      kind: 'match',
      prompt: 'Match each animal with its name.',
      pairs: [
        { left: '🐶', right: 'dog' },
        { left: '🐱', right: 'cat' },
        { left: '🐦', right: 'bird' },
        { left: '🐴', right: 'horse' },
        { left: '🐟', right: 'fish' },
        { left: '🐰', right: 'rabbit' },
      ],
      mistake: 'vocabulary',
    },
  ]),
  ...bank('en.greetings', 2, [
    {
      kind: 'choice',
      prompt: '« Good morning! » — What do you answer?',
      ...opts('Good morning!', m('Good night!', 'comprehension'), m('Goodbye!', 'comprehension')),
    },
    {
      kind: 'choice',
      prompt: '« What’s your name? »',
      ...opts('My name is Sami.', m('I’m ten.', 'comprehension'), m('I’m fine.', 'comprehension')),
    },
    {
      kind: 'choice',
      prompt: '« How old are you? »',
      ...opts(
        'I’m ten.',
        m('My name is Salma.', 'comprehension'),
        m('I’m from Tunisia.', 'comprehension'),
      ),
    },
    {
      kind: 'choice',
      prompt: '« How are you? »',
      ...opts(
        'I’m fine, thank you.',
        m('I’m ten.', 'comprehension'),
        m('Goodbye.', 'comprehension'),
      ),
    },
    {
      kind: 'choice',
      prompt: '« Thank you! »',
      ...opts(
        'You’re welcome.',
        m('Good morning.', 'comprehension'),
        m('I’m fine.', 'comprehension'),
      ),
    },
    {
      kind: 'choice',
      prompt: '« Where are you from? »',
      ...opts(
        'I’m from Tunisia.',
        m('I’m nine.', 'comprehension'),
        m('Yes, I am.', 'comprehension'),
      ),
    },
    {
      kind: 'trueFalse',
      statement: 'We say « Good night » in the morning.',
      answer: false,
      mistake: 'comprehension',
      explanation: 'In the morning we say « Good morning ».',
    },
    { kind: 'trueFalse', statement: 'We say « Goodbye » when we leave.', answer: true },
  ]),
  ...bank('en.to-be', 3, [
    {
      kind: 'choice',
      prompt: 'I ___ a pupil.',
      ...opts('am', m('is', 'conjugation'), m('are', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'She ___ my sister.',
      ...opts('is', m('am', 'conjugation'), m('are', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'They ___ in the garden.',
      ...opts('are', m('is', 'conjugation'), m('am', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'We ___ friends.',
      ...opts('are', m('is', 'conjugation'), m('am', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'It ___ a cat.',
      ...opts('is', m('are', 'conjugation'), m('am', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'You ___ tall.',
      ...opts('are', m('is', 'conjugation'), m('am', 'conjugation')),
    },
    {
      kind: 'trueFalse',
      statement: '« He are happy » is correct.',
      answer: false,
      mistake: 'conjugation',
      explanation: 'We say « He is happy ».',
    },
    { kind: 'trueFalse', statement: '« I am ten » is correct.', answer: true },
  ]),
  ...bank('en.days-months', 2, [
    {
      kind: 'categorize',
      prompt: 'Sort the words.',
      categories: [
        { id: 'days', label: 'Days' },
        { id: 'months', label: 'Months' },
      ],
      entries: [
        { text: 'Monday', category: 'days' },
        { text: 'July', category: 'months' },
        { text: 'Friday', category: 'days' },
        { text: 'March', category: 'months' },
        { text: 'Sunday', category: 'days' },
        { text: 'October', category: 'months' },
        { text: 'Wednesday', category: 'days' },
        { text: 'May', category: 'months' },
      ],
      mistake: 'vocabulary',
    },
    {
      kind: 'order',
      prompt: 'Put the days in order:',
      unit: 'word',
      tokens: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Put the months in order:',
      unit: 'word',
      tokens: ['January', 'February', 'March', 'April'],
      mistake: 'word-order',
    },
    {
      kind: 'choice',
      prompt: 'Which day comes after Friday?',
      ...opts('Saturday', m('Thursday', 'vocabulary'), m('Sunday', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Which month comes after March?',
      ...opts('April', m('May', 'vocabulary'), m('February', 'vocabulary')),
    },
    { kind: 'trueFalse', statement: 'There are seven days in a week.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'There are ten months in a year.',
      answer: false,
      mistake: 'vocabulary',
      explanation: 'There are twelve months in a year.',
    },
  ]),
  ...bank('en.sentences', 3, [
    {
      kind: 'order',
      prompt: 'Put the words in order:',
      unit: 'word',
      tokens: ['I', 'have', 'got', 'a', 'cat.'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Put the words in order:',
      unit: 'word',
      tokens: ['She', 'can', 'swim.'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Put the words in order:',
      unit: 'word',
      tokens: ['There', 'is', 'a', 'book', 'on', 'the', 'table.'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Put the words in order:',
      unit: 'word',
      tokens: ['My', 'brother', 'likes', 'football.'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Put the words in order:',
      unit: 'word',
      tokens: ['We', 'play', 'in', 'the', 'park.'],
      mistake: 'word-order',
    },
  ]),
];

export const lessons = [
  lesson('en.alphabet', 'Capital and small letters', [
    { type: 'intro', text: 'Every English letter has a capital form and a small form.' },
    { type: 'example', text: 'A a, B b, C c, D d…' },
    { type: 'tip', text: 'Careful: b and d look alike. The b has its belly on the right.' },
  ]),
  lesson('en.colours', 'Colours', [
    { type: 'intro', text: 'Let’s learn the colours of things around us.' },
    {
      type: 'example',
      text: 'A banana is yellow. A frog is green. A strawberry is red.',
      visual: { emoji: '🍌 🐸 🍓', label: 'yellow, green, red' },
    },
    { type: 'tip', text: 'Say the colour out loud when you see it.' },
  ]),
  lesson('en.numbers', 'Numbers', [
    { type: 'intro', text: 'Numbers from 13 to 19 end in « -teen ».' },
    { type: 'example', text: 'thirteen (13), fourteen (14), fifteen (15)… and twenty (20).' },
    {
      type: 'rule',
      title: '-teen or -ty?',
      text: 'From 13 to 19, numbers end in « -teen ». 20, 30, 40… end in « -ty ».',
      examples: [
        { expression: '13 → thirteen', text: '30 → thirty' },
        { expression: '16 → sixteen', text: '60 → sixty' },
        { expression: '19 → nineteen', text: '90 → ninety' },
      ],
    },
  ]),
  lesson('en.family-animals', 'My family and my pets', [
    { type: 'intro', text: 'Let’s name the people in our family and our pets.' },
    { type: 'example', text: 'mother, father, sister, brother — dog, cat, bird' },
    { type: 'tip', text: 'Your mother’s mother is your grandmother.' },
  ]),
  lesson('en.greetings', 'Hello!', [
    { type: 'intro', text: 'We greet people and introduce ourselves.' },
    { type: 'example', text: '— Hello! What’s your name?  — My name is Mariem.' },
    { type: 'tip', text: 'Listen to the question word: what, how, where…' },
  ]),
  lesson('en.to-be', 'Am, is, are', [
    { type: 'intro', text: 'The verb « to be » changes with the person.' },
    { type: 'example', text: 'I am — you are — he / she / it is — we are — they are' },
    {
      type: 'rule',
      title: 'Am, is or are?',
      text: 'I → am. He, she, it → is. You, we, they → are.',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '🙋', title: 'I am' },
          { emoji: '👧', title: 'he / she / it is' },
          { emoji: '👨‍👩‍👧', title: 'you / we / they are' },
        ],
      },
      examples: [
        { expression: 'I am seven.' },
        { expression: 'She is my sister.' },
        { expression: 'They are happy.' },
      ],
    },
    { type: 'tip', text: 'Look at the first word of the sentence before you choose.' },
  ]),
  lesson('en.days-months', 'Days and months', [
    { type: 'intro', text: 'A week has seven days and a year has twelve months.' },
    { type: 'example', text: 'Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday' },
    {
      type: 'rule',
      title: 'Capital letters',
      text: 'In English, days, months, names and the word « I » always start with a capital letter.',
      examples: [
        { expression: 'Monday, July', text: 'days and months' },
        { expression: 'Sami lives in Sfax.', text: 'names of people and places' },
        { expression: 'Yes, I can!', text: 'the word « I »' },
      ],
    },
  ]),
  lesson('en.sentences', 'Building sentences', [
    {
      type: 'intro',
      text: 'An English sentence starts with a capital letter and ends with a full stop.',
    },
    { type: 'example', text: 'I have got a cat.' },
    { type: 'tip', text: 'Find the word with a capital letter: it goes first.' },
  ]),
];
